import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api, useSEO } from "@/lib/api";
import { MaskReveal } from "@/components/Reveal";
import { ProductCard } from "@/components/ProductCard";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const TITLES = {
  men: { title: "Men", sub: "Frames engineered for him." },
  women: { title: "Women", sub: "Silhouettes designed for her." },
  "new-arrivals": { title: "New Arrivals", sub: "The latest from the VYRON atelier." },
  "best-sellers": { title: "Best Sellers", sub: "Loved by thousands." },
  rimless: { title: "Rimless", sub: "Weightless, invisible elegance." },
  square: { title: "Square", sub: "Bold, architectural geometry." },
  rectangle: { title: "Rectangle", sub: "Sharp and refined." },
  aviator: { title: "Aviator", sub: "The heritage icon, elevated." },
  "premium-collection": { title: "Premium Collection", sub: "Our finest craftsmanship." },
  "limited-edition": { title: "Limited Edition", sub: "Small batches. Rare finds." },
};

export default function Collection({ preset }) {
  const params = useParams();
  const key = preset || params.slug;
  const meta = TITLES[key] || { title: "Shop", sub: "The full collection." };
  useSEO(meta.title, `Shop VYRON ${meta.title} — ${meta.sub}`);

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("featured");

  useEffect(() => {
    setLoading(true);
    const q =
      key === "men" || key === "women"
        ? `gender=${key}`
        : key === "new-arrivals"
        ? "new_arrival=true"
        : key === "best-sellers"
        ? "best_seller=true"
        : `collection=${key}`;
    api.get(`/products?${q}`).then((r) => setProducts(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [key]);

  const sorted = useMemo(() => {
    const arr = [...products];
    if (sort === "price-low") arr.sort((a, b) => a.price - b.price);
    else if (sort === "price-high") arr.sort((a, b) => b.price - a.price);
    else if (sort === "newest") arr.sort((a, b) => Number(b.new_arrival) - Number(a.new_arrival));
    return arr;
  }, [products, sort]);

  return (
    <div data-testid="collection-page" className="pt-32 pb-28">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <h1 className="font-display text-5xl md:text-7xl tracking-[-0.03em] leading-[0.9]">
              <MaskReveal>{meta.title}</MaskReveal>
            </h1>
            <p className="text-muted-foreground mt-4">{meta.sub}</p>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{sorted.length} items</span>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-[180px] rounded-full border-black/15" data-testid="sort-select">
                <SelectValue placeholder="Sort" />
              </SelectTrigger>
              <SelectContent className="rounded-2xl">
                <SelectItem value="featured">Featured</SelectItem>
                <SelectItem value="newest">Newest</SelectItem>
                <SelectItem value="price-low">Price: Low to High</SelectItem>
                <SelectItem value="price-high">Price: High to Low</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <p className="text-muted-foreground">Loading…</p>
        ) : sorted.length === 0 ? (
          <p className="text-muted-foreground" data-testid="collection-empty">No products in this collection yet.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6" data-testid="collection-grid">
            {sorted.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
          </div>
        )}
      </div>
    </div>
  );
}
